import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { CubeIcon, TrashIcon, PlusIcon, MagnifyingGlassIcon, EyeIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { ButtonGroup } from '../components/ui/button-group';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { confirmDialog } from '../components/ConfirmDialog';
import { Tip } from '../components/ui/tip';
import AssetFormModal from '../components/AssetFormModal';
import { Asset, CLASES, AREAS, AREA_LABEL, ESTADOS, ESTADO_LABEL } from './assetsShared';

const AssetsPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('manage_assets') || hasPermission('manage_settings');

  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [fClase, setFClase] = useState('all');
  const [fArea, setFArea] = useState('all');
  const [fEstado, setFEstado] = useState('all');
  const [search, setSearch] = useState('');
  const [showNew, setShowNew] = useState(false);

  const fetchAssets = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (fClase !== 'all') p.set('clase', fClase);
      if (fArea !== 'all') p.set('area', fArea);
      if (fEstado !== 'all') p.set('estado', fEstado);
      if (search.trim()) p.set('search', search.trim());
      const res = await axios.get(`/api/assets?${p.toString()}`);
      setAssets(res.data.assets || []);
    } catch { toast.error('Error al cargar equipos'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAssets(); /* eslint-disable-next-line */ }, [fClase, fArea, fEstado]);

  const filtered = assets.filter(a => !search.trim() ||
    a.code.toLowerCase().includes(search.toLowerCase()) ||
    a.description.toLowerCase().includes(search.toLowerCase()) ||
    (a.marca || '').toLowerCase().includes(search.toLowerCase()) ||
    (a.modelo || '').toLowerCase().includes(search.toLowerCase()));

  const remove = async (a: Asset, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!(await confirmDialog({ title: 'Eliminar equipo', description: `¿Eliminar ${a.code} — ${a.description}? No se puede deshacer.`, confirmText: 'Eliminar' }))) return;
    try { await axios.delete(`/api/assets/${a.id}`); toast.success('Eliminado'); setAssets(prev => prev.filter(x => x.id !== a.id)); }
    catch { toast.error('Error al eliminar'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-start">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Equipos y herramientas</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Inventario de activos · norma MA-CAL-02 (CLASE-ÁREA-Nº)</p>
        </div>
        {canManage && (
          <Button onClick={() => setShowNew(true)} size="sm" className="h-8 rounded-lg gap-1.5 px-3 bg-black dark:bg-white text-white dark:text-black hover:bg-black/90 dark:hover:bg-white/90 font-bold">
            <PlusIcon className="h-4 w-4 mr-1" /> Nuevo equipo
          </Button>
        )}
      </div>

      <div className="relative">
        <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none z-10" />
        <Input placeholder="Buscar por código, descripción o modelo..." value={search} onChange={e => setSearch(e.target.value)} className="h-8 w-full sm:w-[320px] lg:w-[520px] pl-8 rounded-lg" />
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="space-y-1 w-full sm:w-[160px]">
          <Label className="text-xs text-muted-foreground">Clase</Label>
          <Select value={fClase} onValueChange={setFClase}>
            <SelectTrigger className="h-8 rounded-lg"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Todas</SelectItem>{CLASES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1 w-full sm:w-[220px]">
          <Label className="text-xs text-muted-foreground">Área</Label>
          <Select value={fArea} onValueChange={setFArea}>
            <SelectTrigger className="h-8 rounded-lg"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Todas</SelectItem>{AREAS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1 w-full sm:w-[180px]">
          <Label className="text-xs text-muted-foreground">Estado</Label>
          <Select value={fEstado} onValueChange={setFEstado}>
            <SelectTrigger className="h-8 rounded-lg"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Todos</SelectItem>{ESTADOS.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <Card><CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12"><CubeIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" /><p className="text-muted-foreground">No hay equipos cargados</p></div>
        ) : (
          <div className="relative w-full overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="hover:bg-muted/50 border-b">
                <TableHead className="h-10 px-2 font-medium text-left">Código</TableHead>
                <TableHead className="h-10 px-2 font-medium text-left">Descripción</TableHead>
                <TableHead className="hidden md:table-cell h-10 px-2 font-medium text-left">Área</TableHead>
                <TableHead className="hidden lg:table-cell h-10 px-2 font-medium text-left">Ubicación</TableHead>
                <TableHead className="hidden sm:table-cell h-10 px-2 font-medium text-left">Estado</TableHead>
                <TableHead className="h-10 px-2 font-medium text-left"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filtered.map(a => (
                  <TableRow key={a.id} className="border-b hover:bg-muted/50 cursor-pointer" onClick={() => navigate(`/assets/${a.id}`)}>
                    <TableCell className="p-2 align-middle whitespace-nowrap"><span className="font-mono text-sm font-medium">{a.code}</span></TableCell>
                    <TableCell className="p-2 align-middle max-w-[260px]">
                      <div className="font-medium truncate">{a.description}</div>
                      {(a.marca || a.modelo) && <div className="text-xs text-muted-foreground truncate">{[a.marca, a.modelo].filter(Boolean).join(' · ')}</div>}
                    </TableCell>
                    <TableCell className="hidden md:table-cell p-2 align-middle whitespace-nowrap"><Badge variant="secondary" className="text-xs">{AREA_LABEL[a.area] || a.area}</Badge></TableCell>
                    <TableCell className="hidden lg:table-cell p-2 align-middle whitespace-nowrap text-sm text-muted-foreground">{a.ubicacionFisica || '—'}</TableCell>
                    <TableCell className="hidden sm:table-cell p-2 align-middle whitespace-nowrap">
                      {(() => {
                        const dot = a.estado === 'operativo' ? 'bg-emerald-500'
                          : a.estado === 'en_reparacion' ? 'bg-amber-500'
                          : a.estado === 'borrador' ? 'bg-blue-500'
                          : 'bg-slate-400';
                        return (
                          <span className="inline-flex items-center gap-2 text-sm">
                            <span className={`inline-block h-2.5 w-2.5 rounded-full ${dot} ${a.estado === 'operativo' ? 'animate-pulse' : ''}`} />
                            {ESTADO_LABEL[a.estado] || a.estado}
                          </span>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="p-2 align-middle whitespace-nowrap text-right" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-end">
                        <ButtonGroup>
                          <Tip label="Ver"><Button variant="outline" size="icon" className="size-8" onClick={() => navigate(`/assets/${a.id}`)}><EyeIcon className="h-4 w-4" /></Button></Tip>
                          {a.fichaDocId && <Tip label="Ver ficha técnica"><Button variant="outline" size="icon" className="size-8" onClick={() => window.open(`/api/manuals/${a.fichaDocId}/file?token=${localStorage.getItem('token') || ''}`, '_blank')}><DocumentTextIcon className="h-4 w-4" /></Button></Tip>}
                          {canManage && <Tip label="Eliminar"><Button variant="outline" size="icon" className="size-8" onClick={(e) => remove(a, e)}><TrashIcon className="h-4 w-4" /></Button></Tip>}
                        </ButtonGroup>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent></Card>

      <AssetFormModal open={showNew} editAsset={null} onClose={() => setShowNew(false)} onSaved={fetchAssets} />
    </div>
  );
};

export default AssetsPage;
